import reportErrorSentry from "src/lib/reportErrorSentry";
import { cacheGet, cachePut } from "./fileIntakeCache";
import { convertHeicToJpeg, isHeicFile } from "./heicConverter";
import { downscaleImage } from "./imageDownscale";

const jpegNameFor = (name: string): string =>
  name.replace(/\.(heic|heif)$/i, ".jpg");

/**
 * Converts HEIC files to JPEG if possible.
 * This represents an abstraction layer for the file uploads => should be applied to all file uploads.
 * @param file
 */
export const intakeFile = async (file: File): Promise<File> => {
  const cached = cacheGet(file);
  if (cached) {
    return cached;
  }

  // Passthrough for non HEIC files
  if (!(await isHeicFile(file))) {
    cachePut(file, file);
    return file;
  }

  try {
    const jpeg = await convertHeicToJpeg(file);
    const converted = new File([jpeg], jpegNameFor(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified
    });
    cachePut(file, converted);
    return converted;
  } catch (e) {
    reportErrorSentry(e);
    return file;
  }
};

export const intakeFiles = async (files: Iterable<File>): Promise<File[]> =>
  Promise.all(Array.from(files, intakeFile));

/**
 * Intake for photos: additionally caps the image so a phone camera shot fits
 * under the upload limit. Non-images pass through untouched.
 *
 * Not for documents — an insurance card or lab report needs its full resolution
 * to stay legible, so those keep using {@link intakeFile}.
 */
export const intakeImageFile = async (file: File): Promise<File> =>
  downscaleImage(await intakeFile(file));
